import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosDelete, axiosGet, axiosPost, axiosPut } from '../api/axios';

export interface ContentRecommendation {
  id: number;
  title: string;
  description: string;
  questions: string[];
  vertical_id: number | null;
  vertical_name: string | null;
  created_at: string;
}

interface ContentRecommendationListResponse {
  count: number;
  data: ContentRecommendation[];
}

export type ContentRecommendationPayload = {
  title: string;
  description: string;
  questions: string[];
  vertical_id: number | null;
};

export const useContentRecommendations = () =>
  useQuery<ContentRecommendation[]>(['content-recommendations'], async () => {
    const response = await axiosGet('/content_recommendations/', {}, 'v3');
    const body = response.data as ContentRecommendationListResponse;
    return body.data ?? [];
  });

export const useCreateContentRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: ContentRecommendationPayload) => axiosPost('/content_recommendations/', payload, 'v3'), {
    onSuccess: () => {
      queryClient.invalidateQueries(['content-recommendations']);
    },
  });
};

export const useUpdateContentRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload }: { id: number; payload: ContentRecommendationPayload }) =>
      axiosPut(`/content_recommendations/${id}`, payload, 'v3'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['content-recommendations']);
      },
    },
  );
};

export const useDeleteContentRecommendation = () => {
  const queryClient = useQueryClient();
  return useMutation((id: number) => axiosDelete(`/content_recommendations/${id}`, {}, 'v3'), {
    onSettled: () => {
      queryClient.invalidateQueries(['content-recommendations']);
    },
  });
};
